import { useState, useCallback, useRef } from "react";
import axios from "axios";

import { API } from "./config/api";
const adviceCache = {};

function buildCacheKey(field, rounded, cycleNum, context) {
  const ctxPart = Object.keys(context)
    .sort()
    .map(k => `${k}:${context[k]}`)
    .join("|");
  return `${field}_${rounded}_cycle${cycleNum}__${ctxPart}`;
}

export function useAIAdvice() {
  const [advice, setAdvice] = useState(null);
  const [status, setStatus] = useState("info");
  const [icon, setIcon] = useState("ℹ️");
  const [loading, setLoading] = useState(false);

  const activeKeyRef = useRef(null);
  const requestIdRef = useRef(0);

  const fetchAdvice = useCallback(async (
    field, value, unit, cycleNum,
    context = {},
    allFields = []
  ) => {
    if (value == null || !field) return;

    const rounded = typeof value === "number" ? Math.round(value * 100) / 100 : value;
    const cacheKey = buildCacheKey(field, rounded, cycleNum, context);

    if (activeKeyRef.current === cacheKey) return;
    activeKeyRef.current = cacheKey;

    if (adviceCache[cacheKey]) {
      const cached = adviceCache[cacheKey];
      setAdvice(cached.text);
      setStatus(cached.status);
      setIcon(cached.icon);
      setLoading(false);
      return;
    }

    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    setAdvice(null);

    try {
      const fieldsInChart = allFields.length > 0
        ? allFields.map(f => {
          const label = f.label || f.field;
          const val = f.value != null ? (typeof f.value === 'number' ? Math.round(f.value * 100) / 100 : f.value) : '';
          const unit = f.unit || '';
          return `${label}: ${val} ${unit}`.trim();
        }).join("\n")
        : Object.entries(context)
          .filter(([k]) => k !== "cycle_num")
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n");

      const res = await axios.post(`${API}/ai-advice`, {
        field,
        value: rounded,
        unit,
        cycle_num: cycleNum,
        context,
        fields_in_chart: fieldsInChart,
        all_fields: allFields.length > 0 ? allFields : undefined,
      });

      if (myRequestId !== requestIdRef.current) return;
      const result = {
        text: res.data.advice,
        status: res.data.status || "info",
        icon: res.data.icon || "ℹ️"
      };

      adviceCache[cacheKey] = result;
      setAdvice(result.text);
      setStatus(result.status);
      setIcon(result.icon);
    } catch (e) {
      if (myRequestId !== requestIdRef.current) return;
      setAdvice("[WARN] Could not load AI advice.");
      setStatus("error");
      setIcon("❌");
    }

    if (myRequestId === requestIdRef.current) {
      setLoading(false);
    }
  }, []);

  const clearAdvice = useCallback(() => {
    activeKeyRef.current = null;
  }, []);

  return { advice, status, icon, loading, fetchAdvice, clearAdvice };
}